import { useForm } from "react-hook-form";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useUtils } from "@/app/hooks/useUtils";
import { IRainSettings, IWeatherReason } from "../interface";

interface IRainSettingsFieldsProps {
  value: IRainSettings;
  onChange: (next: IRainSettings) => void;
  error?: string | null;
  reasons: IWeatherReason[];
}

const RainSettingsFields = ({ value, onChange, error, reasons }: IRainSettingsFieldsProps) => {
  const { register, formState } = useForm();
  const { addMinutesAndCalculateTime } = useUtils();
  const handleSelectItem = (item: any) => {
    if (!item) return;
    const matched = reasons.find((r) => r.reason === item.id);
    if (matched) {
      onChange({
        ...value,
        isRainy: matched.is_raining,
        isEventActive: matched.is_event_active,
        reason: matched.reason,
      });
    }
  };

  // End time may be at most 1 hour after the start time (applies to every condition).
  const endMaxTime = value.startTime ? addMinutesAndCalculateTime(value.startTime, 60).newTime : "";
  const clampEnd = (t: string) => (endMaxTime && t > endMaxTime ? endMaxTime : t);

  const currentReason = reasons.find((r) => {
    if (r.is_raining) return value.isRainy;
    if (r.is_event_active) return !!value.isEventActive;
    return !value.isRainy && !value.isEventActive;
  });

  const defaultSelected = currentReason
    ? {
        title: currentReason.reason === "Rain" ? "Rainy" : currentReason.reason,
        id: currentReason.reason,
      }
    : null;
  const isTimeRequired = value.isRainy || !!value.isEventActive;

  return (
    <div>
      <div className="flex items-center">
        <label className="w-[110px] text-black text-xs font-medium">Condition</label>
        <div className="h-9 flex-1">
          <NonSearchableSelectMenu
            selectItems={reasons.map((r) => ({
              title: r.reason === "Rain" ? "Rainy" : r.reason,
              id: r.reason,
            }))}
            onSelectItem={handleSelectItem}
            defaultSelected={defaultSelected || undefined}
            placeholder="Select Condition"
            className="text-xs"
            showDropdownIcon
            dropdownPosition="bottom"
            listZIndex="z-50"
            errorMessages={[]}
            fieldError={formState.errors.condition}
            register={register("condition")}
          />
        </div>
      </div>

      <div className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="flex items-center">
          <label className="w-[98px] text-black text-xs">
            Start Time {isTimeRequired && <span className="text-rose-600">*</span>}
          </label>
          <input
            type="time"
            value={value.startTime}
            onChange={(e) => onChange({ ...value, startTime: e.target.value })}
            className="h-9 flex-1 rounded-md border border-slate-300 px-3 text-sm focus:border-[#193A6A] focus:outline-none"
          />
        </div>
        <div className="flex items-center">
          <label className="w-[98px] text-black text-xs">
            End Time {isTimeRequired && <span className="text-rose-600">*</span>}
          </label>
          <input
            type="time"
            value={value.endTime}
            min={value.startTime || undefined}
            max={endMaxTime || undefined}
            onChange={(e) => onChange({ ...value, endTime: clampEnd(e.target.value) })}
            className="h-9 flex-1 rounded-md border border-slate-300 px-3 text-sm focus:border-[#193A6A] focus:outline-none"
          />
        </div>
        {error && <p className="text-[11px] text-rose-600">{error}</p>}
      </div>
    </div>
  );
};

export default RainSettingsFields;

